import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Button, List, Avatar, Tag, Rate, message, Popover, Statistic, Modal, Tabs } from 'antd';
import { TeamOutlined, UserOutlined, StarOutlined, SettingOutlined, MessageOutlined, TrophyOutlined, CalendarOutlined, EyeOutlined, InfoCircleOutlined, MailOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { absoluteUploadUrl } from '../../utils/uploadUrl';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import ConvitesPanel from '../../components/community/ConvitesPanel';

const { Title, Text } = Typography;

const TeamDashboard = () => {
  const [teamProfile, setTeamProfile] = useState(null);
  const [nearbyPlayers, setNearbyPlayers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [pendingRatings, setPendingRatings] = useState([]);
  const [stats, setStats] = useState({});
  const [playerModal, setPlayerModal] = useState({ visible: false, player: null });
  const [welcomeModal, setWelcomeModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadTeamProfile();
    loadNearbyPlayers();
    loadInvites();
    loadPendingRatings();
    
    // Verificar se é o primeiro acesso à comunidade
    const hasSeenWelcome = localStorage.getItem('community_welcome_seen');
    if (!hasSeenWelcome) {
      setWelcomeModal(true);
    }
    
    // Escutar notificações para atualizar dados
    const handleStorageChange = (e) => {
      if (e.key === 'notification_update') {
        loadInvites();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const loadTeamProfile = async () => {
    try {
      const response = await api.get('/community/teams/profile', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTeamProfile(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setTeamProfile({ exists: false });
      }
    }
  };

  const loadNearbyPlayers = async () => {
    try {
      const response = await api.get('/community/teams/nearby-players', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setNearbyPlayers(response.data.players);
    } catch (error) {
      console.error('Erro ao carregar jogadores:', error);
    }
  };

  const loadInvites = async () => {
    try {
      const response = await api.get('/community/invites/team', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { limit: 50 }
      });
      console.log('Candidaturas carregadas:', response.data);
      setInvites(response.data);
    } catch (error) {
      console.error('Erro ao carregar convites:', error);
    }
  };

  const loadPlayerDetails = async (playerId) => {
    try {
      const response = await api.get(`/community/players/${playerId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPlayerModal({ visible: true, player: response.data });
    } catch (error) {
      message.error('Erro ao carregar dados do jogador');
    }
  };

  const loadPendingRatings = async () => {
    try {
      const response = await api.get('/community/ratings/pending', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPendingRatings(response.data);
    } catch (error) {
      console.error('Erro ao carregar avaliações:', error);
    }
  };

  const handleRespondApplication = async (inviteId, status) => {
    try {
      await api.put(`/community/invites/${inviteId}/respond`, { status }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      message.success(status === 'ACCEPTED' ? 'Candidatura aceita!' : 'Candidatura recusada');
      loadInvites();
      // Notificar outras páginas para atualizar
      localStorage.setItem('notification_update', Date.now());
    } catch (error) {
      message.error('Erro ao responder candidatura');
    }
  };

  const getSkillColor = (level) => {
    const colors = {
      iniciante: 'green',
      recreativo: 'blue',
      intermediario: 'orange',
      avancado: 'red',
      semi_pro: 'purple'
    };
    return colors[level] || 'default';
  };

  const handleWelcomeClose = () => {
    localStorage.setItem('community_welcome_seen', 'true');
    setWelcomeModal(false);
  };

  if (teamProfile && teamProfile.exists === false) {
    return (
      <div style={{ padding: '20px' }}>
        <Card style={{ textAlign: 'center' }}>
          <TeamOutlined style={{ fontSize: 64, color: '#1890ff', marginBottom: 16 }} />
          <Title level={3}>Bem-vindo à Comunidade!</Title>
          <Text>Crie o perfil do seu time para começar a encontrar jogadores e participar da comunidade.</Text>
          <div style={{ marginTop: 24 }}>
            <Button type="primary" size="large" onClick={() => navigate('/community/team/profile')}>
              Criar Perfil do Time
            </Button>
          </div>
        </Card>
        
        {/* Modal de Boas-vindas à Comunidade */}
        <Modal
          title={<><InfoCircleOutlined style={{ color: '#1890ff' }} /> Bem-vindo à Comunidade Futezap!</>}
          open={welcomeModal}
          onCancel={handleWelcomeClose}
          footer={[
            <Button key="start" type="primary" onClick={handleWelcomeClose}>
              Começar a Usar
            </Button>
          ]}
          width={600}
        >
          <div style={{ padding: '16px 0' }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <TeamOutlined style={{ fontSize: 48, color: '#1890ff' }} />
            </div>
            
            <Typography.Paragraph style={{ fontSize: 16, textAlign: 'center', marginBottom: 24 }}>
              A <strong>Comunidade Futezap</strong> conecta organizadores e jogadores, facilitando a formação de times e a participação em jogos.
            </Typography.Paragraph>
            
            <div style={{ marginBottom: 16 }}>
              <Typography.Title level={5}>
                <UserOutlined style={{ color: '#52c41a' }} /> Como Organizador, você pode:
              </Typography.Title>
              <ul style={{ paddingLeft: 20 }}>
                <li>Criar o perfil do seu time e torná-lo visível na comunidade</li>
                <li>Encontrar jogadores próximos no mapa interativo</li>
                <li>Receber candidaturas de jogadores interessados</li>
                <li>Convidar jogadores específicos para seus jogos</li>
                <li>Avaliar jogadores após as partidas</li>
                <li>Gerenciar convites e candidaturas em tempo real</li>
              </ul>
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <Typography.Title level={5}>
                <StarOutlined style={{ color: '#faad14' }} /> Benefícios da Comunidade:
              </Typography.Title>
              <ul style={{ paddingLeft: 20 }}>
                <li><strong>Notificações em tempo real</strong> - Receba alertas instantâneos</li>
                <li><strong>Sistema de avaliações</strong> - Construa reputação e confiança</li>
                <li><strong>Mapa interativo</strong> - Encontre jogadores por localização e posição</li>
                <li><strong>Gestão simplificada</strong> - Organize convites e candidaturas facilmente</li>
              </ul>
            </div>
            
            <div style={{ 
              backgroundColor: '#f6ffed', 
              border: '1px solid #b7eb8f', 
              borderRadius: 6, 
              padding: 16, 
              textAlign: 'center' 
            }}>
              <Typography.Text strong style={{ color: '#389e0d' }}>
                💡 Dica: Comece criando o perfil do seu time para aparecer na comunidade!
              </Typography.Text>
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  if (!teamProfile) return <div>Carregando...</div>;

  const painelContent = (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Jogadores Próximos"
              value={nearbyPlayers.length}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Candidaturas Pendentes"
              value={invites.filter(i => !i.invited_by_team && i.status === 'PENDING').length}
              prefix={<MessageOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Avaliações Pendentes"
              value={pendingRatings.length}
              prefix={<StarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Avaliação do Time"
              value={teamProfile.average_rating || 0}
              precision={1}
              prefix={<TrophyOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <Card 
            title={<><SettingOutlined /> Perfil do Time</>}
            extra={<Button type="link" onClick={() => navigate('/community/team/edit')}>Editar</Button>}
          >
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Avatar size={80} src={absoluteUploadUrl(teamProfile.logo_url)} icon={<TeamOutlined />} />
              <Title level={4} style={{ margin: '8px 0' }}>{teamProfile.name}</Title>
              <Tag color={getSkillColor(teamProfile.skill_level)}>{teamProfile.skill_level}</Tag>
              {teamProfile.average_rating > 0 && (
                <div style={{ marginTop: 8 }}>
                  <Rate disabled value={teamProfile.average_rating} />
                  <Text type="secondary"> ({teamProfile.total_ratings} avaliações)</Text>
                </div>
              )}
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <Text strong>Público: </Text>
              <Tag color={teamProfile.is_public ? 'green' : 'red'}>
                {teamProfile.is_public ? 'Sim' : 'Não'}
              </Tag>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card 
            title={<><UserOutlined /> Jogadores Próximos</>}
            extra={<Button type="link" onClick={() => navigate('/community/map')}>Ver Mapa</Button>}
          >
            <List
              dataSource={nearbyPlayers.slice(0, 5)}
              renderItem={player => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<Avatar src={player.profile_photo} icon={<UserOutlined />} />}
                    title={player.name}
                    description={
                      <div>
                        <div>{player.preferred_position} - {player.neighborhood}</div>
                        <Tag color={getSkillColor(player.skill_level)}>{player.skill_level}</Tag>
                        {player.average_rating > 0 && <Rate disabled value={player.average_rating} />}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card 
            title="Candidaturas Recebidas" 
            extra={<Button type="link" onClick={() => navigate('/community/invites/history?type=received')}>Ver Histórico</Button>}
            style={{ marginBottom: 16 }}
          >
            <List
              dataSource={invites.filter(i => !i.invited_by_team).slice(0, 7)}
              renderItem={invite => (
                <List.Item style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    <List.Item.Meta
                      title={invite.player_name}
                      description={`${invite.preferred_position} - ${invite.game_date || 'Data não informada'}`}
                      style={{ flex: 1 }}
                    />
                    {invite.status !== 'PENDING' && (
                      <Tag color={invite.status === 'ACCEPTED' ? 'green' : 'red'}>
                        {invite.status === 'ACCEPTED' ? '✓ Aceita' : '✕ Recusada'}
                      </Tag>
                    )}
                  </div>
                  {invite.status === 'PENDING' && (
                    <div style={{ 
                      display: 'flex', 
                      gap: 8, 
                      marginTop: 8, 
                      justifyContent: 'flex-end',
                      flexWrap: 'wrap'
                    }}>
                      <Button 
                        size="small" 
                        icon={<EyeOutlined />} 
                        onClick={() => loadPlayerDetails(invite.player_id)}
                      >
                        Perfil
                      </Button>
                      {invite.message && (
                        <Popover 
                          content={<div style={{ maxWidth: 200 }}>{invite.message}</div>}
                          title="Mensagem do jogador"
                          trigger="click"
                        >
                          <Button size="small" icon={<MessageOutlined />}>
                            Mensagem
                          </Button>
                        </Popover>
                      )}
                      <Button 
                        size="small" 
                        type="primary" 
                        onClick={() => handleRespondApplication(invite.id, 'ACCEPTED')}
                      >
                        Aceitar
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => handleRespondApplication(invite.id, 'REJECTED')}
                      >
                        Recusar
                      </Button>
                    </div>
                  )}
                </List.Item>
              )}
            />
          </Card>

          <Card 
            title="Candidaturas Enviadas" 
            extra={<Button type="link" onClick={() => navigate('/community/invites/history?type=sent')}>Ver Histórico</Button>}
            style={{ marginBottom: 16 }}
          >
            <List
              dataSource={invites.filter(i => i.invited_by_team).slice(0, 7)}
              renderItem={invite => (
                <List.Item
                  actions={[
                    <Tag color={
                      invite.status === 'PENDING' ? 'orange' :
                      invite.status === 'ACCEPTED' ? 'green' : 'red'
                    }>
                      {invite.status === 'PENDING' ? 'Pendente' :
                       invite.status === 'ACCEPTED' ? '✓ Aceito' : '✕ Recusado'}
                    </Tag>
                  ]}
                >
                  <List.Item.Meta
                    title={invite.player_name}
                    description={`${invite.preferred_position} - ${invite.game_date || 'Data não informada'}`}
                  />
                </List.Item>
              )}
            />
          </Card>

          <Card title={<><StarOutlined /> Avaliações Pendentes</>}>
            <List
              dataSource={pendingRatings.slice(0, 3)}
              renderItem={game => (
                <List.Item>
                  <List.Item.Meta
                    title={game.player_name}
                    description={`Jogo de ${game.game_date}`}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </>
  );

  const tabItems = [
    { key: 'painel', label: <><SettingOutlined /> Painel</>, children: painelContent },
    { key: 'invites', label: <><MailOutlined /> Convites</>, children: <ConvitesPanel userType="organizer" /> },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <Title level={2}>Dashboard da Comunidade</Title>

      <Tabs defaultActiveKey="painel" items={tabItems} />

      {/* Modal de Boas-vindas à Comunidade */}
      <Modal
        title={<><InfoCircleOutlined style={{ color: '#1890ff' }} /> Bem-vindo à Comunidade Futezap!</>}
        open={welcomeModal}
        onCancel={handleWelcomeClose}
        footer={[
          <Button key="start" type="primary" onClick={handleWelcomeClose}>
            Começar a Usar
          </Button>
        ]}
        width={600}
      >
        <div style={{ padding: '16px 0' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <TeamOutlined style={{ fontSize: 48, color: '#1890ff' }} />
          </div>
          
          <Typography.Paragraph style={{ fontSize: 16, textAlign: 'center', marginBottom: 24 }}>
            A <strong>Comunidade Futezap</strong> conecta organizadores e jogadores, facilitando a formação de times e a participação em jogos.
          </Typography.Paragraph>
          
          <div style={{ marginBottom: 16 }}>
            <Typography.Title level={5}>
              <UserOutlined style={{ color: '#52c41a' }} /> Como Organizador, você pode:
            </Typography.Title>
            <ul style={{ paddingLeft: 20 }}>
              <li>Criar o perfil do seu time e torná-lo visível na comunidade</li>
              <li><strong>Compartilhar o link de cadastro</strong> com jogadores para eles se registrarem</li>
              <li>Encontrar jogadores próximos no mapa interativo</li>
              <li>Receber candidaturas de jogadores interessados</li>
              <li>Convidar jogadores específicos para seus jogos</li>
              <li>Avaliar jogadores após as partidas</li>
              <li>Gerenciar convites e candidaturas em tempo real</li>
            </ul>
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <Typography.Title level={5}>
              <StarOutlined style={{ color: '#faad14' }} /> Benefícios da Comunidade:
            </Typography.Title>
            <ul style={{ paddingLeft: 20 }}>
              <li><strong>Notificações em tempo real</strong> - Receba alertas instantâneos</li>
              <li><strong>Sistema de avaliações</strong> - Construa reputação e confiança</li>
              <li><strong>Mapa interativo</strong> - Encontre jogadores por localização e posição</li>
              <li><strong>Gestão simplificada</strong> - Organize convites e candidaturas facilmente</li>
            </ul>
          </div>
          
          <div style={{ 
            backgroundColor: '#fff7e6', 
            border: '1px solid #ffd591', 
            borderRadius: 6, 
            padding: 16, 
            marginBottom: 16
          }}>
            <Typography.Title level={5} style={{ color: '#d46b08', margin: '0 0 8px 0' }}>
              👥 Cadastro de Jogadores
            </Typography.Title>
            <Typography.Text style={{ color: '#d46b08' }}>
              Compartilhe o link <strong>https://admin.futezap.com.br/player/register</strong> com seus jogadores para eles se cadastrarem na comunidade e poderem se candidatar aos seus jogos.
            </Typography.Text>
          </div>
          
          <div style={{ 
            backgroundColor: '#f6ffed', 
            border: '1px solid #b7eb8f', 
            borderRadius: 6, 
            padding: 16, 
            textAlign: 'center' 
          }}>
            <Typography.Text strong style={{ color: '#389e0d' }}>
              💡 Dica: Comece criando o perfil do seu time para aparecer na comunidade!
            </Typography.Text>
          </div>
        </div>
      </Modal>

      {/* Modal de Detalhes do Jogador */}
      <Modal
        title="Detalhes do Jogador"
        open={playerModal.visible}
        onCancel={() => setPlayerModal({ visible: false, player: null })}
        footer={null}
        width={600}
      >
        {playerModal.player && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Avatar size={80} src={playerModal.player.profile_photo} icon={<UserOutlined />} />
              <Title level={4} style={{ margin: '8px 0' }}>{playerModal.player.name}</Title>
              <Tag color={getSkillColor(playerModal.player.skill_level)}>{playerModal.player.skill_level}</Tag>
              {playerModal.player.average_rating > 0 && (
                <div style={{ marginTop: 8 }}>
                  <Rate disabled value={playerModal.player.average_rating} />
                  <Text type="secondary"> ({playerModal.player.total_games} jogos)</Text>
                </div>
              )}
            </div>
            
            <Row gutter={16}>
              <Col span={12}>
                <p><strong>Posição:</strong> {playerModal.player.preferred_position}</p>
                <p><strong>Idade:</strong> {playerModal.player.age} anos</p>
                <p><strong>Telefone:</strong> {playerModal.player.phone}</p>
              </Col>
              <Col span={12}>
                <p><strong>Cidade:</strong> {playerModal.player.city}</p>
                <p><strong>Bairro:</strong> {playerModal.player.neighborhood}</p>
                <p><strong>Disponível:</strong> {playerModal.player.is_available ? 'Sim' : 'Não'}</p>
              </Col>
            </Row>
            
            {playerModal.player.bio && (
              <div style={{ marginTop: 16 }}>
                <p><strong>Sobre:</strong></p>
                <p>{playerModal.player.bio}</p>
              </div>
            )}
            
            {playerModal.player.availability && (
              <div style={{ marginTop: 16 }}>
                <p><strong>Disponibilidade:</strong></p>
                <p>{playerModal.player.availability}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TeamDashboard;